<template>
  <div class="container">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>人事管理</el-breadcrumb-item>
      <el-breadcrumb-item>通讯录</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="main-card">
      <div slot="header" class="toolbar">
        <span class="card-header">
          企微通讯录
          <span class="header-summary">共 {{ totalMembers }} 名成员 · {{ departments.length }} 个部门 · 数据随考勤通讯录同步任务更新（每日 07:10）</span>
        </span>
        <span class="toolbar-right">
          <span class="toolbar-tip">最近同步：{{ fmtTime(syncedAt) }}</span>
          <el-button type="primary" size="medium" icon="el-icon-refresh" style="margin-left: 12px;" :loading="syncing" @click="handleSync">{{ syncing ? '同步中…' : '立即同步' }}</el-button>
        </span>
      </div>

      <el-row :gutter="16">
        <!-- 左侧部门树 -->
        <el-col :span="6">
          <div class="tree-panel">
            <div class="tree-header">
              <span>部门结构</span>
              <el-button type="text" size="mini" @click="handleNodeClick(null)">全部成员</el-button>
            </div>
            <el-tree
              ref="depTree"
              :data="treeData"
              node-key="departmentId"
              :props="{ label: 'label', children: 'children' }"
              highlight-current
              :expand-on-click-node="false"
              default-expand-all
              @node-click="handleNodeClick"
            >
              <span slot-scope="{ data }" class="tree-node">
                <span class="tree-name" :title="data.name">{{ data.name }}</span>
                <span class="tree-count">{{ data.memberCount }}</span>
              </span>
            </el-tree>
            <div v-if="!departments.length && !loadingDeps" class="tree-empty">暂无部门数据，请先在「考勤配置」页同步通讯录</div>
          </div>
        </el-col>

        <!-- 右侧成员列表 -->
        <el-col :span="18">
          <div class="filter-row">
            <el-input
              v-model="filterKeyword"
              placeholder="姓名 / userid"
              clearable
              size="medium"
              style="width: 200px;"
              @keyup.enter.native="handleFilterChange"
              @clear="handleFilterChange"
            ></el-input>
            <el-tag v-if="currentDepartment" closable size="medium" style="margin-left: 10px;" @close="handleNodeClick(null)">
              {{ currentDepartment.name }}（{{ currentDepartment.memberCount }} 人）
            </el-tag>
            <el-button type="primary" size="medium" style="margin-left: 10px;" @click="handleFilterChange">查询</el-button>
            <span class="toolbar-tip" style="margin-left: auto;">共 {{ total }} 条</span>
          </div>

          <el-table v-loading="loading" :data="list" border stripe max-height="580">
            <el-table-column label="#" type="index" width="50" :index="i => (pagenum - 1) * pagesize + i + 1"></el-table-column>
            <el-table-column label="姓名" width="110">
              <template slot-scope="scope">
                <span class="project-name" :class="{ 'departed-name': scope.row.departed }">{{ scope.row.name }}</span>
              </template>
            </el-table-column>
            <el-table-column label="userid" width="170" show-overflow-tooltip>
              <template slot-scope="scope"><span class="text-content">{{ scope.row.userid }}</span></template>
            </el-table-column>
            <el-table-column label="所属部门" min-width="240" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.departsName || '—' }}</template>
            </el-table-column>
            <el-table-column label="最近同步在册" width="160">
              <template slot-scope="scope"><span class="text-content">{{ fmtTime(scope.row.lastSyncedAt) }}</span></template>
            </el-table-column>
            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tooltip v-if="scope.row.departed" :content="'离开名单：' + fmtTime(scope.row.departedAt) + '（90 天内拉取仍尝试补数，历史考勤以企微后台导出为准）'" placement="top">
                  <el-tag size="small" type="info" effect="plain">已离开</el-tag>
                </el-tooltip>
                <el-tag v-else size="small" type="success" effect="plain">在册</el-tag>
              </template>
            </el-table-column>
          </el-table>

          <el-pagination
            style="margin-top: 15px; text-align: right;"
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            :current-page.sync="pagenum"
            :page-sizes="[10, 20, 50]"
            :page-size.sync="pagesize"
            @current-change="fetchUsers"
            @size-change="handleSizeChange"
          ></el-pagination>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'RenshiContacts',
  data () {
    return {
      // 部门树（departmentList 接口）
      departments: [],
      loadingDeps: false,
      totalMembers: 0,
      syncedAt: null,
      currentDepartment: null, // 点击选中的部门节点（null=全部）
      // 成员列表（userList 接口，服务端分页）
      list: [],
      total: 0,
      loading: false,
      filterKeyword: '',
      pagenum: 1,
      pagesize: 20,
      // 立即同步（异步受理 + 轮询状态，完成后刷新部门树与成员列表；需「考勤配置-同步」权限）
      syncing: false,
      syncPollTimer: null
    }
  },
  computed: {
    /**
     * 部门平铺列表 → 树（parentid 组装；孤儿节点挂到根，防可见范围裁剪导致父级缺失时整枝丢失）
     */
    treeData () {
      const nodes = this.departments.map(d => ({
        departmentId: d.departmentId,
        name: d.name,
        memberCount: d.memberCount,
        children: []
      }))
      const nodeMap = {}
      nodes.forEach(n => { nodeMap[n.departmentId] = n })
      const roots = []
      for (const d of this.departments) {
        const node = nodeMap[d.departmentId]
        const parent = nodeMap[d.parentid]
        if (parent && parent !== node) parent.children.push(node)
        else roots.push(node)
      }
      // 人数含子部门汇总（展示更直观；点选筛选仍为直属口径）
      const fillSum = n => {
        n.label = n.name
        n.sum = n.memberCount + n.children.reduce((s, c) => { fillSum(c); return s + c.sum }, 0)
      }
      roots.forEach(r => { fillSum(r); r.label = r.name })
      return roots
    }
  },
  created () {
    this.fetchDepartments()
    this.fetchUsers()
  },
  beforeDestroy () {
    this.stopSyncPolling()
  },
  methods: {
    async fetchDepartments () {
      this.loadingDeps = true
      try {
        const { data: res } = await this.$http.get('renshi/checkin/departmentList')
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.departments = res.data.list || []
        this.totalMembers = res.data.total || 0
        this.syncedAt = res.data.syncedAt
      } finally {
        this.loadingDeps = false
      }
    },
    async fetchUsers () {
      this.loading = true
      try {
        const params = { pagenum: this.pagenum, pagesize: this.pagesize }
        if (this.filterKeyword.trim()) params.keyword = this.filterKeyword.trim()
        if (this.currentDepartment) params.departmentId = this.currentDepartment.departmentId
        const { data: res } = await this.$http.get('renshi/checkin/userList', { params })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.list = res.data.list || []
        this.total = res.data.total || 0
      } finally {
        this.loading = false
      }
    },
    /**
     * 部门树节点点击（null=全部成员）；直属口径筛选
     */
    handleNodeClick (data) {
      this.currentDepartment = data ? { departmentId: data.departmentId, name: data.name, memberCount: data.memberCount } : null
      this.pagenum = 1
      this.fetchUsers()
    },
    handleFilterChange () {
      this.pagenum = 1
      this.fetchUsers()
    },
    handleSizeChange () {
      this.pagenum = 1
      this.fetchUsers()
    },
    /**
     * 立即同步通讯录（异步受理：与考勤配置页同一入口 syncUsers；
     * 无「考勤配置-同步」权限时后端拦截报错）
     */
    async handleSync () {
      this.syncing = true
      try {
        const { data: res } = await this.$http.post('renshi/checkin/syncUsers')
        if (res.meta.status !== 200) {
          this.syncing = false
          return this.$message.error(res.meta.msg)
        }
        this.startSyncPolling()
      } catch (err) {
        this.syncing = false
        this.$message.error('同步请求失败')
      }
    },
    /**
     * 轮询同步状态（5s；非 running 即完成）：结束后刷新数据并提示结果。
     * 轮询接口无权限（仅授了同步未授查看的罕见场景）时降级为 12s 后直接刷新
     */
    startSyncPolling () {
      this.stopSyncPolling()
      this.syncPollTimer = setInterval(async () => {
        try {
          const { data: res } = await this.$http.get('renshi/checkin/syncStatus')
          if (res.meta.status !== 200) {
            this.stopSyncPolling()
            setTimeout(() => this.finishSync(null), 12000)
            return
          }
          const state = res.data.tasks && res.data.tasks.syncUsers
          if (state && state.status !== 'running') {
            this.stopSyncPolling()
            this.finishSync(state)
          }
        } catch (err) {
          this.stopSyncPolling()
          setTimeout(() => this.finishSync(null), 12000)
        }
      }, 5000)
    },
    stopSyncPolling () {
      if (this.syncPollTimer) {
        clearInterval(this.syncPollTimer)
        this.syncPollTimer = null
      }
    },
    /**
     * 同步收尾：state 有值时按结果提示（成功含成员/部门数），随后刷新部门树与成员列表
     */
    finishSync (state) {
      this.syncing = false
      if (state && state.status === 'success') {
        const s = state.stat || {}
        this.$message.success(`同步完成：${s.total || 0} 名成员 · ${s.departments || 0} 个部门`)
      } else if (state && state.status === 'error') {
        this.$message.error(`同步失败：${state.message}`)
      } else {
        this.$message.info('同步已提交，已为您刷新最新数据')
      }
      this.fetchDepartments()
      this.fetchUsers()
    },
    fmtTime (v) {
      return v ? new Date(v).toLocaleString('sv-SE', { hour12: false }).slice(0, 16) : '—'
    }
  }
}
</script>

<style scoped>
.main-card {
  margin-top: 20px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.toolbar-right {
  display: flex;
  align-items: center;
}

.card-header {
  font-size: 16px;
  font-weight: bold;
}
.header-summary {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 12px;
}
.toolbar-tip {
  color: #909399;
  font-size: 12px;
}

/* 左侧部门树面板 */
.tree-panel {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 8px;
  min-height: 480px;
}
.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px 10px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid #EBEEF5;
  margin-bottom: 8px;
}
.tree-node {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding-right: 8px;
  overflow: hidden;
}
.tree-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tree-count {
  color: #909399;
  font-size: 12px;
  margin-left: 8px;
}
.tree-empty {
  color: #c0c4cc;
  font-size: 12px;
  text-align: center;
  padding: 24px 0;
}

.filter-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.text-content {
  font-size: 12px;
  color: #909399;
}
.project-name {
  font-size: 13px;
  color: #303133;
}
.departed-name {
  color: #909399;
}
</style>
